// ============================================================================
// GESTIMMONY — Page /payment/success (retour Stripe checkout)
// ============================================================================
// Poll /api/payments/status/{session_id} toutes les 2 s (max 15 tentatives)
// jusqu'à obtenir payment_status == "paid". Puis redirige vers /app.
// ============================================================================
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

const MAX_POLLS = 15;
const POLL_MS = 2000;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("polling"); // polling | paid | timeout | error
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!sid) { setStatus("error"); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setPlan(data.lookup_key);
          setStatus("paid");
          // Rafraîchit le contexte auth pour que l'abonnement fraîchement
          // activé débloque immédiatement l'accès à l'espace.
          refresh();
          return;
        }
      } catch (e) {
        // ignore transient errors during polling
      }
      if (attempts >= MAX_POLLS) { setStatus("timeout"); return; }
      setTimeout(poll, POLL_MS);
    };
    poll();
    return () => { cancelled = true; };
  }, [sid]);

  const planLabel = {
    gestimmony_essentiel_monthly: "Essentiel",
    gestimmony_patrimoine_monthly: "Patrimoine",
    gestimmony_prestige_monthly: "Prestige",
    gestimmony_essentiel_annual: "Essentiel (annuel)",
    gestimmony_patrimoine_annual: "Patrimoine (annuel)",
    gestimmony_prestige_annual: "Prestige (annuel)",
  }[plan] || "";

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/50 to-white flex items-center justify-center px-4" data-testid="payment-success-page">
      <Card className="max-w-lg w-full p-8 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="h-14 w-14 text-teal-600 mx-auto animate-spin mb-4" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Confirmation en cours…</h1>
            <p className="text-sm text-slate-500 mt-2">
              Nous validons votre paiement auprès de Stripe. Cela prend quelques secondes.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="h-16 w-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="payment-success-title">
              Merci ! 🎉
            </h1>
            <p className="text-slate-600 mt-3">
              Votre abonnement <b>{planLabel}</b> est actif. Vous pouvez maintenant profiter de toutes les fonctionnalités.
            </p>
            <Button
              onClick={() => navigate("/app")}
              className="mt-6 bg-[hsl(var(--primary))]"
              data-testid="payment-success-cta"
            >
              Ouvrir mon espace <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="h-14 w-14 text-amber-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Paiement en cours de validation</h1>
            <p className="text-sm text-slate-500 mt-2">
              La confirmation prend plus de temps que prévu. Nous vous enverrons un email dès que c'est bon.
            </p>
            <Link to="/app" className="inline-block mt-6">
              <Button variant="outline">Aller à mon espace</Button>
            </Link>
          </>
        )}
        {status === "error" && (
          <>
            <div className="h-16 w-16 rounded-full bg-rose-100 mx-auto flex items-center justify-center mb-4">
              <XCircle className="h-9 w-9 text-rose-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Session invalide</h1>
            <p className="text-sm text-slate-500 mt-2">
              Impossible de retrouver votre paiement. Contactez-nous si vous avez été prélevé.
            </p>
            <Link to="/tarifs" className="inline-block mt-6">
              <Button variant="outline">Voir les tarifs</Button>
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
